"use client";

import { createContext, useContext, useMemo } from "react";
import type { Environment, Theme } from "../../../types";
import type { OnOutlink } from "../../../lib/outlink";
import type {
  EstimateGasFn,
  LpBalanceReaderFn,
  SendTransactionFn,
  StakingRewardsReaderFn,
} from "../types";
import { DEFAULT_PORTFOLIO_FIAT, type PortfolioFiat } from "../utils";

export interface WalletPortfolioContextValue {
  env: Environment;
  theme: Theme;
  open: boolean;
  walletAddress: string;
  close: () => void;
  /**
   * game-swap LP 섹션이 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 dapp-ui가 CROSS 체인
   * RPC로 직접 읽는 내부 폴백으로 동작한다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션이 온체인 `earned()` / `balanceOf()`를 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 API
   * `cumulative_earned` / `total_stake`로 폴백한다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  /**
   * 포트폴리오 섹션(Rewards / Forge / DEX Order 등) 내부 `<a target="_blank">`
   * 클릭을 가로채 목적지 URL을 변형하거나 취소할 수 있는 콜백.
   * `handleOutlinkClick` 유틸을 통해 각 섹션에서 일관되게 호출된다.
   */
  onOutlink?: OnOutlink;
  /**
   * 내장 Withdraw(Rewards) 등 포트폴리오 안에서 트랜잭션을 직접 보낼 때
   * 사용하는 서명/브로드캐스트 콜백. host(wagmi 보유 측)의
   * `sendTransactionAsync`를 그대로 전달해도 호환된다.
   * 미주입 시 해당 액션은 기존 외부 링크로 폴백한다.
   */
  sendTransaction?: SendTransactionFn;
  /** 내장 Withdraw 확인 화면의 가스 추정 콜백. 미주입 시 지갑 추정에 위임. */
  estimateGas?: EstimateGasFn;
  /**
   * 포트폴리오 금액 표시 통화의 USD 환산 비율(USD 기준). 예) USD=1, EUR=0.92.
   * 미주입/비유효(<=0) 시 1(=USD 원 금액)로 취급한다.
   */
  conversionRatio?: number;
  /** 포트폴리오 금액 표시 통화 기호. 미주입 시 `"$"`. */
  currencySymbol?: string;
}

export const WalletPortfolioContext =
  createContext<WalletPortfolioContextValue | null>(null);

export function useWalletPortfolioContext() {
  const ctx = useContext(WalletPortfolioContext);
  if (!ctx) {
    throw new Error(
      "WalletPortfolio compound components must be used within <WalletPortfolio>",
    );
  }
  return ctx;
}

/**
 * 컨텍스트의 `conversionRatio`/`currencySymbol`를 금액 포맷 유틸이 쓰는
 * {@link PortfolioFiat} 형태로 정규화해 반환한다. 미주입 시 USD 기본값.
 */
export function usePortfolioFiat(): PortfolioFiat {
  const { conversionRatio, currencySymbol } = useWalletPortfolioContext();
  // rate/symbol이 바뀔 때만 새 객체를 만들어, 소비 측 useMemo/의존성이
  // 매 렌더 무효화되지 않도록 참조 안정성을 유지한다.
  return useMemo<PortfolioFiat>(
    () => ({
      rate:
        typeof conversionRatio === "number" && conversionRatio > 0
          ? conversionRatio
          : DEFAULT_PORTFOLIO_FIAT.rate,
      symbol: currencySymbol || DEFAULT_PORTFOLIO_FIAT.symbol,
    }),
    [conversionRatio, currencySymbol],
  );
}
